import { useEffect, useRef, useState } from '@wordpress/element';
import { createPortal } from 'react-dom';
import styles from "./SelectDropDown.module.css";

const SelectDropDown = ({ 
  label, 
  id, 
  value, 
  options, 
  onChange 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calculate dropdown position when open
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 250; // Max height of dropdown
      
      let top;
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        // Position above
        top = rect.top + scrollTop - dropdownHeight - 5;
      } else {
        // Position below
        top = rect.bottom + scrollTop + 5;
      }
      
      setDropdownPosition({
        top: top,
        left: rect.left + scrollLeft,
        width: rect.width
      });
    }
  }, [isOpen]);

  const handleSelectChange = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={styles["hvnly_settings_api__form-group"]}>
      {label && (
        <label 
          htmlFor={id} 
          className={styles["hvnly_settings_api__form-label"]}
        >
          {label}
        </label>
      )}

      {/* Dropdown trigger */}
      <div 
        ref={triggerRef}
        className={`${styles["hvnly_settings_api__form-control"]} ${styles.custom_dropdown}`}
        onClick={toggleDropdown}
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedOption?.label || "Select an option"}</span>
        <svg 
          className={`${styles.dropdown_icon} ${isOpen ? styles.rotate : ""}`} 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Portal dropdown - renders at body level */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className={styles.dropdown_options_portal}
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999
          }}
          role="listbox"
          aria-labelledby={id}
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`${styles.option} ${value === opt.value ? styles.selected : ""}`}
              onClick={() => handleSelectChange(opt.value)}
              role="option"
              aria-selected={value === opt.value}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SelectDropDown;